import { CommonContentWrapper } from "../../common/components/content_wrapper/CommonContentWrapper";
import { Padding, Row, SizedBox } from "../../common/components/CommonBaseComponents";
import { PageTitle } from "../../common/components/PageTitle";
import { useEffect, useState } from "react";
import { CustomDateFilterButton } from "../../common/components/button/CustomDateFilterButton";
import { CustomTable, LinkTd, ResultStatus } from "../../common/components/table/CustomTable";
import SearchBar from "../../common/components/input/SearchBar";
import SecondaryButton from "../../common/components/button/SecondaryButton";
import Translate from "../../common/translate/Translate";
import PaymentFilterButton from "../../common/components/filter_button/PaymentFilterButton";
import TransactionStatusFilterButton from "../../common/components/filter_button/TransactionStatusFilterButton";
import { RevenueDetailModal } from "./modals/RevenueDetailModal";
import { useDevice } from "../../common/hooks/useDevice";
import { useWindowSize } from "../../common/hooks/useWindowSize";
import { useSearchParams } from "react-router-dom";
import RevenueService from "../../service/RevenueService";

/**
 * CustomDateFilterButton의 값을 API용 startDate/endDate(yyyy-MM-dd)로 변환
 */
const parseDateFilter = (dateFilter) => {
    const today = new Date();
    const fmt = (d) => d.toISOString().slice(0, 10);
    const endDate = fmt(today);
    let start = new Date(today);

    if (dateFilter === "All Date") {
        start.setFullYear(start.getFullYear() - 1);
    } else if (dateFilter === "Today") {
        // 어제 ~ 오늘 (기본값)
        start.setDate(start.getDate() - 1);
    } else if (dateFilter === "7 Days") {
        start.setDate(start.getDate() - 7);
    } else if (dateFilter === "30 Days") {
        start.setDate(start.getDate() - 30);
    } else if (dateFilter.includes(" - ")) {
        const parts = dateFilter.split(" - ");
        return {
            startDate: (parts[0] || ""),
            endDate: (parts[1] || ""),
        };
    } else {
        start.setDate(start.getDate() - 7);
    }
    return { startDate: fmt(start), endDate };
};

export function CoinTransactionListPage() {
    const service = new RevenueService();
    const [searchParams, setSearchParams] = useSearchParams();
    const page = Number(searchParams.get("page")) || 1;

    const [dateFilter, setDateFilter] = useState("All Date");
    const [statusResultFilter, setStatusResultFilter] = useState(["all"]);
    const [statusPaymentFilter, setStatusPaymentFilter] = useState(["all"]);
    const [searchFilter, setSearchFilter] = useState("Transaction ID");
    const [searchValue, setSearchValue] = useState("");
    const [openDetailModal, setOpenDetailModal] = useState(false);
    const [orderList, setOrderList] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [detailDto, setDetailDto] = useState({
        transactionId: "",
        shopName: "",
        shopCode: "",
        deviceName: "",
        payment: "",
        amount: "",
        result: "",
        registerdDate: "",
        device: { deviceNumber: "-", type: "-", model: "-", deviceId: "-" },
    });

    const fetchOrderList = (currentPage, dateFilterValue, searchFilterKey, searchVal, resultFilter) => {
        const { startDate, endDate } = parseDateFilter(dateFilterValue);

        const params = {
            startDate,
            endDate,
            page: currentPage,
            pageSize: 20,
        };

        if (searchVal) {
            if (searchFilterKey === "Transaction ID") {
                params.orderId = searchVal;
            } else if (searchFilterKey === "Shop Code") {
                params.shopCode = searchVal;
            } else if (searchFilterKey === "Shop Name") {
                params.shopName = searchVal;
            } else if (searchFilterKey === "Device Name") {
                params.deviceName = searchVal;
            }
        }

        if (resultFilter && !resultFilter.includes("all") && resultFilter.length > 0) {
            params.result = resultFilter.join(",");
        }

        service
            .getRevenueOrderList(params)
            .then((result) => {
                const existingParams = Object.fromEntries(searchParams.entries());
                setSearchParams(
                    { ...existingParams, totalPage: result?.page?.total || 1 },
                    { replace: true }
                );
                setOrderList(result?.orderInfo || []);
                setTotalCount(result?.page?.totalElements || 0);
            })
            .catch((error) => {
                console.error("Error fetching coin order list:", error);
            });
    };

    useEffect(() => {
        fetchOrderList(page, dateFilter, searchFilter, searchValue, statusResultFilter);
    }, [page]);

    useEffect(() => {
        fetchOrderList(1, dateFilter, searchFilter, searchValue, statusResultFilter);
    }, [dateFilter, statusResultFilter]);

    const onSearch = () => {
        const existingParams = Object.fromEntries(searchParams.entries());
        setSearchParams({ ...existingParams, page: 1 }, { replace: true });
        fetchOrderList(1, dateFilter, searchFilter, searchValue, statusResultFilter);
    };

    const tableHeaders = [
        {
            key: "transactionID",
            label: Translate("revenue.transactionId"),
            render: (row) => (
                <LinkTd
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        setDetailDto({
                            transactionId: row.orderId || row.transactionId || "",
                            shopName: row.shopName || "",
                            shopCode: row.shopCode || "",
                            deviceName: row.deviceName || "",
                            payment: row.payment || "",
                            amount: row.amount || "",
                            result: row.result || "",
                            registerdDate: row.registeredDate || "",
                            device: row.device || {
                                name: "-",
                                type: "-",
                                id: "-",
                                modelId: "-",
                                activatedDate: "-",
                            },
                        });
                        setOpenDetailModal(true);
                    }}
                >
                    {row.orderId || row.transactionId}
                </LinkTd>
            ),
        },
        {
            key: "shopCode",
            label: Translate("store.shopCode"),
        },
        {
            key: "shopName",
            label: Translate("store.shopName"),
        },
        {
            key: "deviceName",
            label: Translate("common.deviceName"),
        },
        {
            key: "payment",
            label: Translate("revenue.paymentMethod"),
        },
        {
            key: "amount",
            label: Translate("revenue.amount"),
        },
        {
            key: "result",
            label: Translate("revenue.result"),
            render: (row) => <ResultStatus>{row.result}</ResultStatus>,
        },
        {
            key: "registeredDate",
            label: Translate("common.registeredDate"),
        },
    ];

    const currentDevice = useDevice();
    const { width } = useWindowSize();

    const pageTitle = <PageTitle title={Translate("gnb.transactionList")} contentLength={totalCount} />;
    const filterButtons = (
        <Row>
            <CustomDateFilterButton currentValue={dateFilter} setValue={setDateFilter} />
            <SizedBox width={"10px"} />
            <PaymentFilterButton currentValues={statusPaymentFilter} setValues={setStatusPaymentFilter} />
            <SizedBox width={"10px"} />
            <TransactionStatusFilterButton currentValues={statusResultFilter} setValues={setStatusResultFilter} />
        </Row>
    );
    const searchBar = (
        <SearchBar
            type={"transactionList"}
            currentValue={searchFilter}
            setValue={setSearchFilter}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            onSearchButtonClick={onSearch}
            totalWidth={currentDevice === "mobile" || currentDevice === "tablet" ? "100%" : undefined}
            selectorWidth={currentDevice === "mobile" || currentDevice === "tablet" ? "20%" : undefined}
            inputWidth={currentDevice === "mobile" || currentDevice === "tablet" ? "70%" : undefined}
        />
    );

    let headerSection = (
        <>
            {pageTitle}
            <SizedBox height={"24px"} />
            <Row justifyContent={"space-between"}>
                <Row>
                    {searchBar}
                    <SizedBox width={"10px"} />
                    {filterButtons}
                </Row>
                {width > 1110 && <SecondaryButton icon={"download"} text={Translate("common.exportExcel")} />}
            </Row>
            <SizedBox height={"20px"} />
        </>
    );

    if (currentDevice === "mobile" || currentDevice === "tablet") {
        headerSection = (
            <>
                {pageTitle}
                <SizedBox height={"24px"} />
                {searchBar}
                <SizedBox height={"10px"} />
                {filterButtons}
                <SizedBox height={"20px"} />
            </>
        );
    }

    return (
        <CommonContentWrapper hasPagination>
            <Padding padding={"24px 20px"}>
                {headerSection}
                <CustomTable headers={tableHeaders} data={orderList} />
            </Padding>
            {openDetailModal && (
                <RevenueDetailModal
                    isOpen={openDetailModal}
                    onClose={() => setOpenDetailModal(false)}
                    dto={detailDto}
                    title={"Vending / Coin Transaction Detail"}
                />
            )}
        </CommonContentWrapper>
    );
}
